// frontend/src/recording/RecordingContext.js
//
// Globalt inspelningskontext (beslut 2026-08-06): inspelningen ägs av en
// provider OVANFÖR routern och överlever därför all navigering i appen —
// användaren kan boka i kalendern, öppna andra journaler, ta betalt i
// kassan eller byta till ett annat program medan dikteringen pågår.
// (Webbläsare undantar flikar med aktiv mikrofonupptagning från
// bakgrundsstrypning, så fönsterbyten är säkra.)
//
// RecordingPanel (i journalvyn) och GlobalRecordingBar (på alla sidor)
// är bara UI-skal mot det här kontextet.
//
// Vid "stoppa och generera" sparas den genererade texten ALLTID till
// journalutkastet server-side (PUT) — även om användaren är någon
// annanstans i appen när genereringen blir klar. Ingen text kan gå
// förlorad för att man klickat vidare.

import React, {
  createContext, useContext, useState, useRef, useEffect, useCallback,
} from 'react';
import TranscriptionArea from '../components/TranscriptionArea/TranscriptionArea';
import { useRecording } from '../hooks/useRecording';
import apiClient from '../apiClient';
import { parseJournalNote } from './journalParse';
import {
  createNotesRecordingSession,
  uploadNotesChunk,
  completeNotesSession,
  getNotesSessionData,
  generateNotesJournal,
  clearNotesSession,
} from '../notesClient';

const CHUNK_START_AFTER = 30; // sekunder innan första chunken
const CHUNK_INTERVAL = 30;    // sekunder mellan chunkar

const RecordingSessionContext = createContext(null);

export const useRecordingSession = () => useContext(RecordingSessionContext);

export function RecordingProvider({ children }) {
  // idle | recording | processing | generating | done | error
  const [phase, setPhase] = useState('idle');
  const [entryId, setEntryId] = useState(null);
  const [patientId, setPatientId] = useState(null);
  const [patientName, setPatientName] = useState('');
  // Mall som används vid generering — kan bytas UNDER pågående inspelning
  const [template, setTemplateState] = useState(null); // {ref, name, sectionTitles}
  const [transcript, setTranscript] = useState('');
  const [uploadFailures, setUploadFailures] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);

  // Mikrofoner (lazy — behörighet begärs först när panelen behöver listan,
  // inte vid appstart)
  const [microphones, setMicrophones] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState('');

  const recording = useRecording();

  const transcriptionAreaRef = useRef(null);
  const sessionIdRef = useRef(null);
  const transcriptRef = useRef('');
  const lastChunkSecondRef = useRef(-1);
  const templateRef_ = useRef(null);        // senaste template (undviker stale closure)
  const entryIdRef = useRef(null);
  const templateChangedRef = useRef(false);
  // Panelen kan "binda" sig till aktiv entry för att få genererat innehåll
  // direkt i editorn; är ingen bunden PUT:as bara server-side.
  const boundHandlerRef = useRef(null);     // {entryId, onContent(contentObj)}
  // Utkastets innehåll FÖRE första genereringen — "Generera om" mergar mot
  // baselinen så att förra genereringen ERSÄTTS istället för att dubbleras.
  const preGenSectionsRef = useRef(null);
  // Journalvyns autospar (Spår J) registrerar en flush-funktion så att lokalt
  // skriven text sparas INNAN baslinjen hämtas inför genereringen.
  const flushHandlerRef = useRef(null);     // {entryId, flush(), getContent()}

  useEffect(() => { templateRef_.current = template; }, [template]);
  useEffect(() => { entryIdRef.current = entryId; }, [entryId]);

  const loadMicrophones = useCallback(async () => {
    // Har vi redan listan behövs ingen ny behörighetsfråga (Firefox frågar
    // annars om vid VARJE getUserMedia om användaren inte valt "Kom ihåg")
    if (microphones.length > 0) return true;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter((d) => d.kind === 'audioinput' && d.label);
      const seen = new Set();
      const unique = [];
      for (const m of mics) {
        const label = m.label.trim();
        if (!seen.has(label)) {
          seen.add(label);
          unique.push(m);
        }
      }
      setMicrophones(unique);
      return true;
    } catch (err) {
      console.error('Kunde inte lista mikrofoner:', err);
      return false;
    }
  }, [microphones.length]);

  const appendTranscript = useCallback((text) => {
    if (!text || !text.trim()) return;
    transcriptRef.current += (transcriptRef.current ? ' ' : '') + text.trim();
    setTranscript(transcriptRef.current);
  }, []);

  const uploadBlob = useCallback(async (blob) => {
    if (!sessionIdRef.current || !blob) return;
    try {
      const partial = await uploadNotesChunk(sessionIdRef.current, blob);
      appendTranscript(partial);
    } catch (err) {
      console.error('Chunk-uppladdning misslyckades:', err);
      setUploadFailures((n) => n + 1);
    }
  }, [appendTranscript]);

  // Chunk-export var 30:e sekund
  useEffect(() => {
    const t = recording.currentTime;
    if (
      phase === 'recording' &&
      recording.isRecording &&
      t >= CHUNK_START_AFTER &&
      (t - CHUNK_START_AFTER) % CHUNK_INTERVAL === 0 &&
      t !== lastChunkSecondRef.current
    ) {
      lastChunkSecondRef.current = t;
      if (transcriptionAreaRef.current) {
        transcriptionAreaRef.current.exportChunk().catch((err) =>
          console.error('exportChunk error:', err));
      }
    }
  }, [recording.currentTime, recording.isRecording, phase]);

  // Varna innan fliken stängs mitt i en inspelning
  useEffect(() => {
    if (phase !== 'recording') return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [phase]);

  const start = useCallback(async ({ entryId: eid, patientId: pid, patientName: pname, templateRef, templateName, sectionTitles }) => {
    if (phase === 'recording' || phase === 'processing' || phase === 'generating') {
      setErrorMsg('En inspelning pågår redan — stoppa den först.');
      return false;
    }
    setErrorMsg(null);
    setUploadFailures(0);

    // Verifiera mikrofonåtkomst FÖRST (TranscriptionArea sväljer annars felet).
    // Hoppa över om behörigheten redan bevisats (mikrofonlistan laddad) —
    // annars triggar varje extra getUserMedia en ny fråga i Firefox.
    if (microphones.length === 0) {
      try {
        const test = await navigator.mediaDevices.getUserMedia({
          audio: selectedMicId ? { deviceId: { exact: selectedMicId } } : true,
          video: false,
        });
        test.getTracks().forEach((tr) => tr.stop());
      } catch (micErr) {
        console.error('Mikrofonåtkomst nekad:', micErr);
        setErrorMsg('Mikrofonen kunde inte användas. Tillåt mikrofonåtkomst för den här sidan (hänglåset i adressfältet) och försök igen.');
        setPhase('error');
        return false;
      }
    }

    try {
      sessionIdRef.current = await createNotesRecordingSession(templateRef);
      transcriptRef.current = '';
      setTranscript('');
      lastChunkSecondRef.current = -1;
      templateChangedRef.current = false;
      setEntryId(eid);
      setPatientId(pid);
      setPatientName(pname || '');
      setTemplateState({ ref: templateRef, name: templateName, sectionTitles: sectionTitles || [] });
      await transcriptionAreaRef.current.startRecording();
      recording.reset();
      recording.start();
      setPhase('recording');
      return true;
    } catch (err) {
      console.error('Kunde inte starta diktering:', err);
      clearNotesSession();
      setErrorMsg('Kunde inte starta dikteringen. Kontrollera att Aion Notes är igång.');
      setPhase('error');
      return false;
    }
  }, [phase, selectedMicId, recording, microphones.length]);

  /** Byt mall under pågående inspelning — används vid genereringen. */
  const setTemplate = useCallback(({ ref, name, sectionTitles }) => {
    setTemplateState({ ref, name, sectionTitles: sectionTitles || [] });
    templateChangedRef.current = true;
  }, []);

  /**
   * Generera journaltext från transkriptet enligt AKTUELL mall, merga mot
   * baselinen (utkastet före första genereringen) och spara server-side.
   * Används av stopAndGenerate och regenerate ("Generera om"/"Byt mall").
   */
  const runGeneration = useCallback(async () => {
    const eid = entryIdRef.current;
    const tpl = templateRef_.current;
    const text = transcriptRef.current.trim();

    setPhase('generating');
    const fullText = await generateNotesJournal(text, tpl.ref, sessionIdRef.current, null);

    const titles = tpl.sectionTitles.length ? tpl.sectionTitles : ['Journal'];
    let parsed = parseJournalNote(fullText, titles);
    if (!parsed.length || parsed.every((s) => !s.content)) {
      parsed = [{ title: titles[0], content: fullText.trim() }];
    }
    const parsedByTitle = new Map(parsed.map((s) => [s.title, s.content]));
    // Text under okända rubriker får inte tappas — läggs sist i första sektionen
    const unmatched = parsed.filter((s) => !titles.includes(s.title)).map((s) => s.content).join('\n\n');

    // Baseline hämtas EN gång (före första genereringen) — "Generera om"
    // ersätter därmed förra genereringen istället för att dubblera text.
    if (preGenSectionsRef.current === null) {
      const resp = await apiClient.get(`/api/journal/entries/${eid}`);
      preGenSectionsRef.current = resp.data?.content?.sections || [];
    }
    const existingByTitle = new Map(preGenSectionsRef.current.map((s) => [s.title, s.content || '']));

    const mergedSections = titles.map((title, idx) => {
      const existing = (existingByTitle.get(title) || '').trim();
      let generated = (parsedByTitle.get(title) || '').trim();
      if (idx === 0 && unmatched) {
        generated = generated ? generated + '\n\n' + unmatched : unmatched;
      }
      return {
        title,
        content: existing && generated ? existing + '\n\n' + generated : (generated || existing),
        order: idx,
      };
    });

    const putBody = { content: { sections: mergedSections } };
    if (templateChangedRef.current) {
      putBody.template_ref = tpl.ref;
    }
    const putResp = await apiClient.put(`/api/journal/entries/${eid}`, putBody);

    // Är journalvyn öppen på den här entryn? Uppdatera editorn direkt.
    // Versionen följer med så att editorns autospar inte får falsk konflikt.
    const bound = boundHandlerRef.current;
    if (bound && bound.entryId === eid && bound.onContent) {
      bound.onContent(putBody.content, {
        templateRef: tpl.ref,
        templateName: tpl.name,
        version: putResp.data?.version,
      });
    }

    setPhase('done');
  }, []);

  const stopAndGenerate = useCallback(async () => {
    if (phase !== 'recording') return;
    setPhase('processing');
    const elapsedMs = recording.getElapsedTime();
    recording.stop();

    try {
      // Sista ljudsnutten
      const finalBlob = await transcriptionAreaRef.current.stopRecording();
      if (finalBlob) {
        await uploadBlob(finalBlob);
      }
      await completeNotesSession(sessionIdRef.current, elapsedMs);

      // SERVERN ÄR FACIT: hämta sessionens fullständiga transkript från
      // Notes istället för att lita på klientens ackumulerade kopia (som
      // kan ha nollställts av UI-övergångar). Chunk-svaren är bara live-UX.
      try {
        const sessionData = await getNotesSessionData(sessionIdRef.current);
        const serverText = (sessionData.transcription_text || '').trim();
        if (serverText) {
          transcriptRef.current = serverText;
          setTranscript(serverText);
        }
      } catch (fetchErr) {
        console.error('Kunde inte hämta transkript från Notes, använder lokal kopia:', fetchErr);
      }

      const text = transcriptRef.current.trim();
      if (!text) {
        setErrorMsg(uploadFailures > 0
          ? 'Ljudet är uppladdat men transkriberingen är fördröjd — Aion Notes gör om den automatiskt. Sessionen finns i Notes; journalen kan kompletteras när texten är klar.'
          : 'Inget tal uppfattades — ingen journal genererades. Kontrollera att rätt mikrofon är vald.');
        setPhase('error');
        return;
      }

      // Sista autosparningen av lokalt skriven text innan baslinjen hämtas
      // (autosparen är pausad under processing/generating, därav force).
      // Gick sparningen inte igenom (nätverk/konflikt) används editorns
      // lokala innehåll som baslinje — annars skulle texten skrivas över.
      preGenSectionsRef.current = null; // färsk baseline för den här dikteringen
      const fh = flushHandlerRef.current;
      if (fh && fh.entryId === entryIdRef.current) {
        let saved = false;
        try {
          saved = await fh.flush({ force: true });
        } catch (flushErr) {
          console.error('Kunde inte spara utkastet före generering:', flushErr);
        }
        if (!saved && fh.getContent) {
          preGenSectionsRef.current = fh.getContent()?.sections || [];
        }
      }

      await runGeneration();
    } catch (err) {
      console.error('Diktering misslyckades:', err);
      setErrorMsg('Något gick fel efter inspelningen. Ljudet är uppladdat till Aion Notes och texten kan hämtas därifrån.');
      setPhase('error');
    }
  }, [phase, recording, uploadBlob, uploadFailures, runGeneration]);

  /**
   * "Generera om" — kör om genereringen på samma transkript, t.ex. efter
   * mallbyte. Ersätter förra genereringen (mergar mot baselinen).
   */
  const regenerate = useCallback(async () => {
    if (phase !== 'done' && phase !== 'error') return;
    if (!transcriptRef.current.trim() || !entryIdRef.current) return;
    setErrorMsg(null);
    try {
      await runGeneration();
    } catch (err) {
      console.error('Omgenerering misslyckades:', err);
      setErrorMsg('Genereringen misslyckades — försök igen.');
      setPhase('error');
    }
  }, [phase, runGeneration]);

  /** Panelen binder sig till aktiv entry för live-uppdatering av editorn. */
  const bind = useCallback((eid, onContent) => {
    boundHandlerRef.current = { entryId: eid, onContent };
    return () => {
      if (boundHandlerRef.current && boundHandlerRef.current.entryId === eid) {
        boundHandlerRef.current = null;
      }
    };
  }, []);

  /** Journalvyns autospar registrerar sin flush (sista sparning före generering). */
  const registerFlush = useCallback((eid, flush, getContent) => {
    const handler = { entryId: eid, flush, getContent };
    flushHandlerRef.current = handler;
    return () => {
      if (flushHandlerRef.current === handler) flushHandlerRef.current = null;
    };
  }, []);

  /** Nollställ efter done/error (inte under pågående inspelning). */
  const dismiss = useCallback(() => {
    if (phase === 'recording' || phase === 'processing' || phase === 'generating') return;
    setPhase('idle');
    setEntryId(null);
    setPatientId(null);
    setPatientName('');
    setTemplateState(null);
    setTranscript('');
    setUploadFailures(0);
    setErrorMsg(null);
    sessionIdRef.current = null;
    transcriptRef.current = '';
    preGenSectionsRef.current = null;
    recording.reset();
  }, [phase, recording]);

  const value = {
    phase,
    entryId,
    patientId,
    patientName,
    template,
    transcript,
    uploadFailures,
    errorMsg,
    currentTime: recording.currentTime,
    microphones,
    selectedMicId,
    setSelectedMicId,
    loadMicrophones,
    start,
    setTemplate,
    stopAndGenerate,
    regenerate,
    bind,
    registerFlush,
    dismiss,
  };

  return (
    <RecordingSessionContext.Provider value={value}>
      {/* Ljudmotorn bor här — den överlever all navigering */}
      <TranscriptionArea
        ref={transcriptionAreaRef}
        onDataAvailable={uploadBlob}
        selectedMicId={selectedMicId}
      />
      {children}
    </RecordingSessionContext.Provider>
  );
}
